import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "@/context/AuthContext";
import { Copy, Check, Sparkles, Target, Loader2, Linkedin, Instagram, Mail, Upload, FileText, X, File } from "lucide-react";
import { toast } from "sonner";

const TONES = [
    { id: "consultative", label: "Consultative" },
    { id: "friendly", label: "Friendly" },
    { id: "professional", label: "Professional" },
    { id: "bold", label: "Bold" },
    { id: "empathetic", label: "Empathetic" },
];

const INDUSTRIES = [
    { id: "saas", label: "SaaS" },
    { id: "real_estate", label: "Real Estate" },
    { id: "coaching", label: "Coaching" },
    { id: "agency", label: "Agency" },
    { id: "ecommerce", label: "E-commerce" },
    { id: "fitness", label: "Fitness" },
    { id: "finance", label: "Finance" },
    { id: "home_improvement", label: "Home Improvement" },
    { id: "insurance", label: "Insurance" },
    { id: "stem_cell_clinics", label: "Stem Cell Clinics" },
    { id: "medical_coaches", label: "Medical Coaches" },
];

const METHODS = [
    { id: "nepq", label: "NEPQ", sub: "Jeremy Miner · diagnostic questions" },
    { id: "spin", label: "SPIN", sub: "Rackham · Situation/Problem/Implication/Need" },
    { id: "challenger", label: "Challenger", sub: "Teach · Tailor · Take control" },
    { id: "sandler", label: "Sandler", sub: "Pain-based · disqualify early" },
    { id: "meddic", label: "MEDDIC", sub: "Metrics · Economic buyer · Champion" },
    { id: "gap", label: "Gap Selling", sub: "Keenan · quantify the gap in $" },
    { id: "consultative", label: "Consultative", sub: "Advisor-first · ask before telling" },
    { id: "straight_line", label: "Straight Line", sub: "Belfort · high-certainty tonality" },
];

const GOALS = [
    { id: "book_meeting", label: "Book meeting" },
    { id: "qualify", label: "Qualify" },
    { id: "nurture", label: "Nurture" },
];

const PLATFORMS = [
    { id: "linkedin", label: "LinkedIn", icon: Linkedin },
    { id: "instagram", label: "Instagram", icon: Instagram },
    { id: "email", label: "Email", icon: Mail },
];

const Pill = ({ active, onClick, children, testid }) => (
    <button
        type="button"
        onClick={onClick}
        data-testid={testid}
        className={`px-3 py-1.5 text-xs rounded-md border transition-all ${
            active
                ? "border-signal bg-signal/10 text-white"
                : "border-zinc-800 bg-zinc-900/40 text-zinc-400 hover:border-zinc-700 hover:text-white"
        }`}
    >
        {children}
    </button>
);

const MessageCard = ({ title, angle, text, why, testid, onCopy, copied }) => (
    <div className="border border-zinc-900 bg-[#0c0c0c] p-6 fade-up" data-testid={testid}>
        <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
                <div className="text-xs font-mono uppercase tracking-widest text-signal">{title}</div>
                {angle && <div className="text-xs text-zinc-500">· {angle}</div>}
            </div>
            <button
                onClick={onCopy}
                data-testid={`${testid}-copy`}
                className="inline-flex items-center gap-1.5 border border-zinc-800 hover:border-zinc-600 text-zinc-300 hover:text-white px-2.5 py-1 rounded-md text-xs"
            >
                {copied ? <><Check className="w-3 h-3" /> Copied</> : <><Copy className="w-3 h-3" /> Copy</>}
            </button>
        </div>
        <p className="text-zinc-100 leading-relaxed whitespace-pre-wrap mb-3">{text}</p>
        {why && <p className="text-xs text-zinc-500 italic">Why: {why}</p>}
    </div>
);

export default function Outreach() {
    const { authHeaders } = useAuth();
    const navigate = useNavigate();
    const [platform, setPlatform] = useState("linkedin");
    const [industry, setIndustry] = useState("saas");
    const [tone, setTone] = useState("consultative");
    const [methodology, setMethodology] = useState("nepq");
    const [goal, setGoal] = useState("book_meeting");
    const [whatSelling, setWhatSelling] = useState("");
    const [productDetails, setProductDetails] = useState("");
    const [targetPersona, setTargetPersona] = useState("");
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [copied, setCopied] = useState(null);
    const [documents, setDocuments] = useState([]);
    const [selectedDocIds, setSelectedDocIds] = useState([]);
    const [uploadingDoc, setUploadingDoc] = useState(false);

    const loadDocs = async () => {
        try {
            const res = await axios.get(`${API}/outreach/documents`, { headers: authHeaders() });
            setDocuments(res.data || []);
            // auto-select newly uploaded docs by default
            setSelectedDocIds((prev) => {
                const ids = new Set(prev);
                (res.data || []).forEach((d) => ids.add(d.id));
                return Array.from(ids).filter((id) => (res.data || []).some((d) => d.id === id));
            });
        } catch {
            /* ignore */
        }
    };

    useEffect(() => {
        loadDocs(); // eslint-disable-next-line
    }, []);

    const onFileUpload = async (e) => {
        const f = e.target.files?.[0];
        e.target.value = ""; // allow re-selecting the same file later
        if (!f) return;
        const ext = f.name.split(".").pop().toLowerCase();
        if (!["pdf", "docx", "txt", "md"].includes(ext)) {
            toast.error("Only PDF, DOCX, TXT or MD files are supported.");
            return;
        }
        if (f.size > 10 * 1024 * 1024) {
            toast.error("File too big — max 10 MB.");
            return;
        }
        const formData = new FormData();
        formData.append("file", f);
        setUploadingDoc(true);
        try {
            const res = await axios.post(`${API}/outreach/documents`, formData, {
                headers: { ...authHeaders(), "Content-Type": "multipart/form-data" },
            });
            setDocuments((prev) => [res.data, ...prev]);
            setSelectedDocIds((prev) => [...prev, res.data.id]);
            toast.success(`Loaded ${res.data.filename} · ${res.data.char_count.toLocaleString()} chars`);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Upload failed");
        } finally {
            setUploadingDoc(false);
        }
    };

    const removeDoc = async (id) => {
        try {
            await axios.delete(`${API}/outreach/documents/${id}`, { headers: authHeaders() });
            setDocuments((prev) => prev.filter((d) => d.id !== id));
            setSelectedDocIds((prev) => prev.filter((x) => x !== id));
            toast.success("Removed");
        } catch {
            toast.error("Could not remove file");
        }
    };

    const toggleDoc = (id) => {
        setSelectedDocIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
    };

    const generate = async () => {
        if (!whatSelling.trim()) {
            toast.error("Tell me what you're selling first.");
            return;
        }
        setLoading(true);
        setResult(null);
        try {
            const res = await axios.post(
                `${API}/outreach/generate`,
                {
                    platform, industry, tone, methodology, goal,
                    what_selling: whatSelling,
                    product_details: productDetails || null,
                    target_persona: targetPersona || null,
                    document_ids: selectedDocIds.length > 0 ? selectedDocIds : null,
                },
                { headers: authHeaders() }
            );
            setResult(res.data);
            toast.success("Your opener is ready — opening your chat…");
            if (res.data?.conversation_id) {
                setTimeout(() => navigate(`/app/chat/${res.data.conversation_id}`), 300);
            }
        } catch (err) {
            const status = err?.response?.status;
            if (status === 402) {
                toast.error(err?.response?.data?.detail || "Free plan limit reached. Upgrade to Pro.");
            } else {
                toast.error(err?.response?.data?.detail || "Generation failed");
            }
        } finally {
            setLoading(false);
        }
    };

    const copy = async (text, id) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopied(id);
            toast.success("Copied");
            setTimeout(() => setCopied(null), 1200);
        } catch { toast.error("Copy failed"); }
    };

    return (
        <div data-testid="outreach-page">
            <div className="mb-8">
                <div className="text-xs font-mono uppercase tracking-[0.2em] text-signal mb-2">/ COLD OUTREACH</div>
                <h1 className="font-heading font-black text-4xl md:text-5xl tracking-tight">The first message.</h1>
                <p className="text-zinc-400 mt-2">Tell me what you sell + who you're chasing. I'll craft the DM that starts the conversation.</p>
            </div>

            <div className="grid lg:grid-cols-2 gap-6">
                {/* LEFT: INPUT */}
                <div className="border border-zinc-900 bg-[#0c0c0c] p-6 space-y-6">
                    <div>
                        <label className="block text-xs font-mono uppercase tracking-widest text-zinc-500 mb-3">Platform</label>
                        <div className="flex flex-wrap gap-2">
                            {PLATFORMS.map(p => (
                                <Pill key={p.id} active={platform === p.id} onClick={() => setPlatform(p.id)} testid={`platform-${p.id}`}>
                                    <span className="inline-flex items-center gap-1.5"><p.icon className="w-3.5 h-3.5" /> {p.label}</span>
                                </Pill>
                            ))}
                        </div>
                    </div>

                    <div>
                        <label className="block text-xs font-mono uppercase tracking-widest text-zinc-500 mb-3">Industry</label>
                        <div className="flex flex-wrap gap-2">
                            {INDUSTRIES.map(i => (
                                <Pill key={i.id} active={industry === i.id} onClick={() => setIndustry(i.id)} testid={`industry-${i.id}`}>{i.label}</Pill>
                            ))}
                        </div>
                    </div>

                    <div>
                        <label className="block text-xs font-mono uppercase tracking-widest text-zinc-500 mb-3">Tone</label>
                        <div className="flex flex-wrap gap-2">
                            {TONES.map(t => (
                                <Pill key={t.id} active={tone === t.id} onClick={() => setTone(t.id)} testid={`tone-${t.id}`}>{t.label}</Pill>
                            ))}
                        </div>
                    </div>

                    <div>
                        <label className="block text-xs font-mono uppercase tracking-widest text-zinc-500 mb-3">Sales methodology</label>
                        <div className="grid grid-cols-2 gap-2">
                            {METHODS.map(m => (
                                <button
                                    key={m.id}
                                    type="button"
                                    onClick={() => setMethodology(m.id)}
                                    data-testid={`method-${m.id}`}
                                    className={`text-left p-3 rounded-md border transition-colors ${
                                        methodology === m.id
                                            ? "border-signal bg-signal/10"
                                            : "border-zinc-800 hover:border-zinc-700 bg-zinc-900/40"
                                    }`}
                                >
                                    <div className="text-sm font-semibold text-white">{m.label}</div>
                                    <div className="text-[10px] text-zinc-500 mt-0.5">{m.sub}</div>
                                </button>
                            ))}
                        </div>
                    </div>

                    <div>
                        <label className="block text-xs font-mono uppercase tracking-widest text-zinc-500 mb-3">Goal</label>
                        <div className="flex flex-wrap gap-2">
                            {GOALS.map(g => (
                                <Pill key={g.id} active={goal === g.id} onClick={() => setGoal(g.id)} testid={`goal-${g.id}`}>{g.label}</Pill>
                            ))}
                        </div>
                    </div>

                    <div>
                        <label className="block text-xs font-mono uppercase tracking-widest text-zinc-500 mb-3">What are you selling?</label>
                        <textarea
                            data-testid="what-selling"
                            value={whatSelling}
                            onChange={(e) => setWhatSelling(e.target.value)}
                            rows={3}
                            placeholder="e.g. A 90-day high-ticket coaching program for agency owners doing $30k+/mo who want to systemise client delivery."
                            className="w-full bg-zinc-900 border border-zinc-800 rounded-md text-white px-4 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-signal focus:border-signal resize-none"
                        />
                    </div>

                    <div>
                        <label className="block text-xs font-mono uppercase tracking-widest text-zinc-500 mb-3">
                            Product data <span className="text-zinc-600 normal-case">(optional — upload docs or paste text)</span>
                        </label>

                        {/* Upload button + file list */}
                        <div className="mb-3">
                            <label
                                className={`flex items-center justify-center gap-2 border-2 border-dashed rounded-md px-4 py-4 text-sm cursor-pointer transition-colors ${
                                    uploadingDoc
                                        ? "border-signal/50 bg-signal/5 text-signal"
                                        : "border-zinc-800 hover:border-signal hover:bg-signal/5 text-zinc-400 hover:text-white"
                                }`}
                                data-testid="doc-upload-label"
                            >
                                <input
                                    type="file"
                                    accept=".pdf,.docx,.txt,.md"
                                    onChange={onFileUpload}
                                    className="hidden"
                                    data-testid="file-upload"
                                    disabled={uploadingDoc}
                                />
                                {uploadingDoc ? (
                                    <><Loader2 className="w-4 h-4 animate-spin" /> Parsing document…</>
                                ) : (
                                    <><Upload className="w-4 h-4" /> Upload product sheet, brochure or spec (PDF, DOCX, TXT · max 10 MB)</>
                                )}
                            </label>

                            {documents.length > 0 && (
                                <div className="mt-3 space-y-2" data-testid="doc-list">
                                    {documents.map((d) => {
                                        const checked = selectedDocIds.includes(d.id);
                                        const ext = (d.filename.split(".").pop() || "").toLowerCase();
                                        const Icon = ext === "pdf" ? FileText : File;
                                        return (
                                            <div
                                                key={d.id}
                                                data-testid={`doc-${d.id}`}
                                                className={`flex items-center gap-3 border rounded-md px-3 py-2 text-sm transition-colors ${
                                                    checked ? "border-signal/50 bg-signal/5" : "border-zinc-800 bg-zinc-900/40"
                                                }`}
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={checked}
                                                    onChange={() => toggleDoc(d.id)}
                                                    data-testid={`doc-toggle-${d.id}`}
                                                    className="accent-signal w-4 h-4 flex-shrink-0"
                                                />
                                                <Icon className="w-4 h-4 text-signal flex-shrink-0" />
                                                <div className="flex-1 min-w-0">
                                                    <div className="text-white truncate">{d.filename}</div>
                                                    <div className="text-[10px] font-mono text-zinc-500">
                                                        {Math.round(d.size / 1024)} KB · {d.char_count.toLocaleString()} chars extracted
                                                    </div>
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={() => removeDoc(d.id)}
                                                    data-testid={`doc-remove-${d.id}`}
                                                    className="text-zinc-500 hover:text-red-400 p-1"
                                                    title="Remove"
                                                >
                                                    <X className="w-4 h-4" />
                                                </button>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        <textarea
                            data-testid="product-details"
                            value={productDetails}
                            onChange={(e) => setProductDetails(e.target.value)}
                            rows={3}
                            placeholder="Or paste additional context: features, pricing, case-study numbers, differentiators…"
                            className="w-full bg-zinc-900 border border-zinc-800 rounded-md text-white px-4 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-signal focus:border-signal resize-none font-mono"
                        />
                    </div>

                    <div>
                        <label className="block text-xs font-mono uppercase tracking-widest text-zinc-500 mb-3">
                            Target persona <span className="text-zinc-600 normal-case">(optional)</span>
                        </label>
                        <input
                            data-testid="target-persona"
                            value={targetPersona}
                            onChange={(e) => setTargetPersona(e.target.value)}
                            placeholder="e.g. Series B CRO running a 20-person SDR team"
                            className="w-full bg-zinc-900 border border-zinc-800 rounded-md text-white px-4 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-signal focus:border-signal"
                        />
                    </div>

                    <button
                        onClick={generate}
                        disabled={loading}
                        data-testid="outreach-generate-btn"
                        className="w-full bg-signal hover:bg-signal-hover text-white px-6 py-3.5 rounded-md font-semibold flex items-center justify-center gap-2 transition-colors disabled:opacity-60"
                    >
                        {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Crafting your opener…</> : <><Sparkles className="w-4 h-4" /> Write my first message</>}
                    </button>
                </div>

                {/* RIGHT: OUTPUT */}
                <div className="space-y-4">
                    {!result && !loading && (
                        <div className="border border-zinc-900 border-dashed p-16 text-center text-zinc-500">
                            <Target className="w-10 h-10 mx-auto mb-4 text-zinc-700" strokeWidth={1.2} />
                            Your first message + 2 alt openers + follow-ups will appear here.
                        </div>
                    )}

                    {loading && (
                        <div className="border border-zinc-900 bg-[#0c0c0c] p-8 tracing-beam">
                            <div className="h-4 bg-zinc-900 rounded mb-3 w-1/3" />
                            <div className="h-3 bg-zinc-900 rounded mb-2" />
                            <div className="h-3 bg-zinc-900 rounded mb-2 w-11/12" />
                            <div className="h-3 bg-zinc-900 rounded w-3/4" />
                        </div>
                    )}

                    {result && (
                        <>
                            <MessageCard
                                title="FIRST MESSAGE"
                                angle={result.first_message.angle}
                                text={result.first_message.text}
                                why={result.first_message.why}
                                testid="first-message"
                                onCopy={() => copy(result.first_message.text, "first")}
                                copied={copied === "first"}
                            />
                            <MessageCard
                                title="Alt opener 1"
                                angle={result.alt_opener_1.angle}
                                text={result.alt_opener_1.text}
                                why={result.alt_opener_1.why}
                                testid="alt-1"
                                onCopy={() => copy(result.alt_opener_1.text, "alt1")}
                                copied={copied === "alt1"}
                            />
                            <MessageCard
                                title="Alt opener 2"
                                angle={result.alt_opener_2.angle}
                                text={result.alt_opener_2.text}
                                why={result.alt_opener_2.why}
                                testid="alt-2"
                                onCopy={() => copy(result.alt_opener_2.text, "alt2")}
                                copied={copied === "alt2"}
                            />
                            <div className="grid md:grid-cols-2 gap-4">
                                <div className="border border-zinc-900 bg-[#0c0c0c] p-5">
                                    <div className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-2">If they go silent · Day 4</div>
                                    <p className="text-sm text-zinc-100 leading-relaxed mb-3">{result.follow_up_if_no_reply}</p>
                                    <button onClick={() => copy(result.follow_up_if_no_reply, "bump")} data-testid="copy-bump"
                                        className="text-xs text-zinc-400 hover:text-white flex items-center gap-1.5">
                                        {copied === "bump" ? <><Check className="w-3 h-3" /> Copied</> : <><Copy className="w-3 h-3" /> Copy</>}
                                    </button>
                                </div>
                                <div className="border border-zinc-900 bg-[#0c0c0c] p-5">
                                    <div className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-2">If they reply curious</div>
                                    <p className="text-sm text-zinc-100 leading-relaxed mb-3">{result.follow_up_if_interested}</p>
                                    <button onClick={() => copy(result.follow_up_if_interested, "interested")} data-testid="copy-interested"
                                        className="text-xs text-zinc-400 hover:text-white flex items-center gap-1.5">
                                        {copied === "interested" ? <><Check className="w-3 h-3" /> Copied</> : <><Copy className="w-3 h-3" /> Copy</>}
                                    </button>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
